// src/components/.../modifyHWS.component.jsx
import React from "react";
import { usePlayer } from "../../../../Contexts/playerData.context";

// ---------- helpers ----------
/** Normalize a server row into our editor keys */
const normalizeRow = (hws = {}) => ({
  HWSId: hws.HWSId || hws.HWSID || hws.hwsId || "",
  height: hws.HEIGHT ?? "",
  heightDate: hws.HeightDate ?? "",
  heightV: hws.Height_V ?? "",
  weight: hws.WEIGHT ?? "",
  weightDate: hws.WeightDate ?? "",
  weightV: hws.Weight_V ?? "",
  speed40Fastest: hws.SPEED40FASTEST ?? "",
  speeddate: hws.speeddate ?? hws.Speed40_F_Date ?? "",
  speed40FVerified: hws.Speed40_F_Verified ?? "",
  speed40FSurface: hws.Speed40_F_Surface ?? "",
  hwsNote: hws.hwsNote ?? "",
});

/** Denormalize editor keys back to server keys */
const denormalizeRow = (draft = {}) => ({
  HWSId: draft.HWSId,
  HEIGHT: draft.height,
  HeightDate: draft.heightDate,
  Height_V: draft.heightV,
  WEIGHT: draft.weight,
  WeightDate: draft.weightDate,
  Weight_V: draft.weightV,
  SPEED40FASTEST: draft.speed40Fastest,
  speeddate: draft.speeddate, // keep same key they use in history
  Speed40_F_Verified: draft.speed40FVerified,
  Speed40_F_Surface: draft.speed40FSurface,
  hwsNote: draft.hwsNote,
});

// ---------- inline editor ----------
const RowEditor = React.memo(function RowEditor({
  draft,
  onEdit,
  onCancel,
  onSave,
  onDelete,
  disabled,
  deleting,
  onPost, // new prop to trigger "Post to DB" logs
}) {
  return (
    <div className="p-2 rounded border border-1 border-secondary alert-light">
      <h5 className="mb-2 text-center text-primary p-2">This Area Updates the HWS History Table</h5>

      {/* HEIGHT */}
      <div className="d-flex mb-2 gap-2 align-items-center">
        <div className="col-8">
          <input
            type="text"
            className="form-control fs-7"
            placeholder="Height"
            value={draft?.height ?? ""}
            onChange={(e) => onEdit("height", e.target.value)}
            onFocus={(e) => { e.target.select(); }}
            onMouseUp={(e) => e.preventDefault()}
            autoFocus
          />
        </div>
        <div className="col-12">
          <input
            type="date"
            className="form-control fs-7"
            placeholder="Height Date"
            value={draft?.heightDate ?? ""}
            onChange={(e) => onEdit("heightDate", e.target.value)}
          />
        </div>
        <div className="col-6">
          <input
            type="text"
            className="form-control fs-7"
            placeholder="P-V-E"
            value={draft?.heightV ?? ""}
            onChange={(e) => onEdit("heightV", e.target.value)}
          />
        </div>
        <div className="col-10 ms-auto">
          <button
            className="btn btn-outline-primary fs-7 w-100"
            onClick={() => onPost && onPost("height", draft)}
          >
            Post Height To DB
          </button>
        </div>
      </div>

      {/* WEIGHT */}
      <div className="d-flex mb-2 gap-2 align-items-center">
        <div className="col-8">
          <input
            type="text"
            className="form-control fs-7"
            placeholder="Weight"
            value={draft?.weight ?? ""}
            onChange={(e) => onEdit("weight", e.target.value)}
            onFocus={(e) => { e.target.select(); }}
            onMouseUp={(e) => e.preventDefault()}
          />
        </div>
        <div className="col-12">
          <input
            type="date"
            className="form-control fs-7"
            placeholder="Weight Date"
            value={draft?.weightDate ?? ""}
            onChange={(e) => onEdit("weightDate", e.target.value)}
          />
        </div>
        <div className="col-6">
          <input
            type="text"
            className="form-control fs-7"
            placeholder="P-V-E"
            value={draft?.weightV ?? ""}
            onChange={(e) => onEdit("weightV", e.target.value)}
          />
        </div>
        <div className="col-10 ms-auto">
          <button
            className="btn btn-outline-primary fs-7 w-100"
            onClick={() => onPost && onPost("weight", draft)}
          >
            Post Weight To DB
          </button>
        </div>
      </div>

      {/* SPEED */}
      <div className="d-flex mb-2 gap-2 align-items-center">
        <div className="col-8">
          <input
            type="text"
            className="form-control fs-7"
            placeholder="40 Fastest"
            value={draft?.speed40Fastest ?? ""}
            onChange={(e) => onEdit("speed40Fastest", e.target.value)}
            onFocus={(e) => { e.target.select(); }}
            onMouseUp={(e) => e.preventDefault()}
          />
        </div>
        <div className="col-12">
          <input
            type="date"
            className="form-control fs-7"
            placeholder="Speed Date"
            value={draft?.speeddate ?? ""}
            onChange={(e) => onEdit("speeddate", e.target.value)}
          />
        </div>
        <div className="col-6">
          <input
            type="text"
            className="form-control fs-7"
            placeholder="P-V-E"
            value={draft?.speed40FVerified ?? ""}
            onChange={(e) => onEdit("speed40FVerified", e.target.value)}
          />
        </div>
        <div className="col-6">
          <input
            type="text"
            className="form-control fs-7"
            placeholder="Surface"
            value={draft?.speed40FSurface ?? ""}
            onChange={(e) => onEdit("speed40FSurface", e.target.value)}
          />
        </div>
        <div className="col-10 ms-auto">
          <button
            className="btn btn-outline-primary fs-7 w-100"
            onClick={() => onPost && onPost("speed", draft)}
          >
            Post Speed To DB
          </button>
        </div>
      </div>

      {/* NOTE */}
      <div className="d-flex mb-2 gap-2 align-items-start">
        <div className="flex-grow-1">
          <textarea
            rows={3}
            className="form-control fs-7"
            placeholder="HWS Note"
            value={draft?.hwsNote ?? ""}
            onChange={(e) => onEdit("hwsNote", e.target.value)}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="d-flex gap-2 col-48">
        {/* Delete on the left */}
        <button
          className="btn btn-danger"
          onClick={onDelete}
          disabled={disabled || deleting}
        >
          {deleting ? "Deleting..." : "Delete"}
        </button>

        {/* Cancel + Save on the right */}
        <div className="d-flex gap-2 ms-auto flex-grow-1 justify-content-end">
          <button
            className="btn btn-outline-danger"
            onClick={onCancel}
            disabled={disabled}
          >
            Cancel
          </button>
          <button
            className="btn btn-success text-white col-18"
            onClick={onSave}
            disabled={disabled}
          >
            {disabled ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
});

// ---------- main component ----------
const ModifyHWS = ({ onCloseHistory, player }) => {
  const { activePlayer, setPlayerData, playerData, setListLoadID } = usePlayer();

  // prefer context activePlayer.id, fallback to prop player.id, then FBXID
  const playerId =
    activePlayer?.id ??
    player?.id ??
    player?.FBXID ??
    null;

  const updateTheDatabase = async (data) => {
    if (!data || !data.playerId) return;
    try {
      const response = await fetch("https://www.fbxchange.com/ReactPHP/tomBox.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      console.log("Update Result:", result);
    } catch (error) {
      console.error("Error updating database:", error);
    };
  }

  const postSingleField = React.useCallback(
    (field, draftRow) => {
      const payload = denormalizeRow(draftRow || {});
      const foundPlayer = Array.isArray(playerData)
        ? playerData.find(p => p.FBXID === playerId)
        : null;

      if (field === "height") {
        let sendPackage = {
          playerId,
          field,
          Height: payload.HEIGHT,
          HeightDate: payload.HeightDate,
          Height_V: payload.Height_V,
          script: "updateSingleHWSField",
        };

        setPlayerData(prev => prev.map(p => {
          if (p.FBXID !== playerId) return p;
          return {
            ...p,
            // server keys (your history/details use these)
            HEIGHT: payload.HEIGHT,
            HeightDate: payload.HeightDate,
            Height_V: payload.Height_V,
            // UI keys (OneLiner reads these)
            height: payload.HEIGHT,
            heightDate: payload.HeightDate,
            heightV: payload.Height_V,
          };
        }));

        updateTheDatabase(sendPackage);

      }
      if (field === "weight") {
       let sendPackage = {
          playerId, field,
          Weight: payload.WEIGHT,
          WeightDate: payload.WeightDate,
          Weight_V: payload.Weight_V,
          script: "updateSingleHWSField",
        };

        setPlayerData(prev => prev.map(p => p.FBXID !== playerId ? p : ({
          ...p,
          WEIGHT: payload.WEIGHT,
          WeightDate: payload.WeightDate,
          Weight_V: payload.Weight_V,
          weight: payload.WEIGHT,
          weightDate: payload.WeightDate,
          weightV: payload.Weight_V,
        })));
         updateTheDatabase(sendPackage);
      }

      if (field === "speed") {
        let sendPackage = {
          playerId, field,
          Speed40Fastest: payload.SPEED40FASTEST,
          speedDate: payload.speeddate,
          Speed40_F_Verified: payload.Speed40_F_Verified,
          Speed40_F_Surface: payload.Speed40_F_Surface,
          script: "updateSingleHWSField",
        };

        setPlayerData(prev => prev.map(p => p.FBXID !== playerId ? p : ({
          ...p,
          SPEED40FASTEST: payload.SPEED40FASTEST,
          speed40FDate: payload.speeddate,
          speed40FVerified: payload.Speed40_F_Verified,
          speed40FSurface: payload.Speed40_F_Surface,
          speed40Fastest: payload.SPEED40FASTEST,
          // OneLiner displays `speed40FVerified` & `speed40FSurface`
        })));

        updateTheDatabase(sendPackage);
      }


      // When you're ready to actually POST, do it here using `payload` + `field`
      // fetch(ENDPOINT, { method: "POST", body: JSON.stringify({ script: "...", payload, field, playerId }) })
    },
    [playerId]
  );

  const [history, setHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const [editingIndex, setEditingIndex] = React.useState(null);
  const [editDraft, setEditDraft] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const [error, setError] = React.useState("");

  const ENDPOINT = "https://www.fbxchange.com/ReactPHP/tomBox.php";

  // Fetch history (mount & when player changes)
  const reloadHistory = React.useCallback(async () => {
    const fbxid = activePlayer?.FBXID ?? player?.FBXID;
    if (!fbxid) return;
    setLoading(true);
    setError("");

    try {
      const response = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ script: "getHWSHistory", fbxid }),
      });
      const data = await response.json();
      setHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      setError("Failed to load HWS history.");
    } finally {
      setLoading(false);
    }
  }, [activePlayer?.FBXID, player?.FBXID]);

  React.useEffect(() => {
    reloadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlayer?.FBXID, player?.FBXID]);

  const openEditor = React.useCallback(
    (idx) => {
      const row = history[idx];
      setEditingIndex(idx);
      setEditDraft(normalizeRow(row));
      setError("");
    },
    [history]
  );

  const closeEditor = React.useCallback(() => {
    setEditingIndex(null);
    setEditDraft(null);
    setSaving(false);
    setError("");
  }, []);

  // only updates local draft; no API calls per keystroke
  const onEdit = React.useCallback((key, value) => {
    setEditDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  // Build payload for API (includes HWSId + FBXID + all fields)
  const buildSaveRequest = React.useCallback(() => {
    const payload = denormalizeRow(editDraft || {});
    const FBXID = activePlayer?.FBXID ?? player?.FBXID ?? null;
    return {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        script: "updateHWSLine",
        FBXID,
        payload,
      }),
    };
  }, [editDraft, activePlayer?.FBXID, player?.FBXID]);

  // Delete then reload history; return to list view
  const deleteRow = React.useCallback(async () => {
    if (!editDraft?.HWSId) return;
    setDeleting(true);
    setError("");
    try {
      const resp = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          script: "deleteHWSRecord",
          HWSId: editDraft.HWSId,
        }),
      });
      await resp.json().catch(() => ({}));
      await reloadHistory(); // bring back fresh list
      closeEditor();         // exit editor → shows header + list
    } catch (e) {
      setError(e?.message || "Delete failed.");
    } finally {
      setDeleting(false);
    }
  }, [editDraft, reloadHistory, closeEditor]);

  // Save then (optionally) reload; always return to list view
  const saveRow = React.useCallback(async () => {
    if (editingIndex == null || !editDraft) return;
    setSaving(true);
    setError("");
    try {
      const req = buildSaveRequest();
      const resp = await fetch(ENDPOINT, req);
      const data = await resp.json().catch(() => ({}));

      if (Array.isArray(data)) {
        setHistory(data);  // server returned fresh history
      } else {
        await reloadHistory(); // or fetch to be safe
      }
      closeEditor(); // exit editor → shows header + list
    } catch (e) {
      setError(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  }, [editingIndex, editDraft, buildSaveRequest, reloadHistory, closeEditor]);

  const Header = () => (
    <div className="d-flex mb-2 col-48 alert-navy p-2 justify-content-between align-items-center">
      <div className="text-uppercase">HWS History</div>
      <div className="d-flex gap-2">
        {onCloseHistory && (
          <button className="btn btn-outline-dark" onClick={onCloseHistory}>
            Back to HWS
          </button>
        )}
      </div>
    </div>
  );

  const RowView = ({ hws, i }) => (
    <div className="p-2 rounded border border-1 border-secondary mb-1">
      <div className="d-flex align-items-center" style={{ fontSize: '0.65rem', gap: '0.25rem' }}>
        <div className="col-2 fw-bold text-danger">
          {hws.HEIGHT === "0" ? "" : hws.HEIGHT}
        </div>
        <div className="col-5">
          {hws.HeightDate === "0000-00-00" ? "" : hws.HeightDate}
        </div>
        <div className="col-1">{hws.Height_V}</div>

        <div className="col-2 text-center fw-bold text-pr ms-1 text-danger">
          {hws.WEIGHT === "0" ? "" : hws.WEIGHT}
        </div>
        <div className="col-5">
          {hws.WeightDate === "0000-00-00" ? "" : hws.WeightDate}
        </div>
        <div className="col-1">{hws.Weight_V}</div>

        <div className="col-2 text-center fw-bold text-danger ms-1">
          {hws.SPEED40FASTEST === "0" ? "" : hws.SPEED40FASTEST}
        </div>
        <div className="col-5">
          {hws.speeddate === "0000-00-00" ? "" : hws.speeddate}
        </div>
        <div className="col-1">{hws.Speed40_F_Verified}</div>
        <div className="col-3">{hws.Speed40_F_Surface}</div>
        <div className="flex-grow-1 text-truncate" title={hws.hwsNote || "-"}>{hws.hwsNote || "-"}</div>
        <div className="col-2">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => openEditor(i)}
          >
            ?
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="border border-1 border-dark rounded p-2">
      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="col-48">
        {/* When editing, show ONLY the editor; otherwise show header + list */}
        {editingIndex !== null ? (
          <div className="rounded p-2 mb-3">
            <RowEditor
              draft={editDraft}
              onEdit={onEdit}
              onCancel={closeEditor}
              onSave={saveRow}
              onDelete={deleteRow}
              disabled={saving || deleting}
              deleting={deleting}
              onPost={postSingleField} // <-- hooked up
            />
          </div>
        ) : (
          <div className="rounded p-2 mb-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {loading ? (
              <div className="text-center py-5">Loading history…</div>
            ) : history.length === 0 ? (
              <div className="text-center py-5">No HWS history found.</div>
            ) : (
              <>
                <Header />
                {history.map((hws, i) => (
                  <div key={hws.HWSId || i}>
                    <RowView hws={hws} i={i} />
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModifyHWS;
